'use client';

import Image from '@/components/Image';
import SoldOutBlock from '@/components/Product/SoldOutBlock';
import Wish from '@/components/Product/Wish';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { useStore } from '@/providers/app-store-provider';
import { EPlatform } from '@/utils/enums/platform-enum';
import { formatPriceNumberWithSymbol, priceInfo as getPriceInfo } from '@/utils/price';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
// @ts-ignore: Icon library missing type definitions
import { LeftArrowOutlined, RightArrowOutlined } from '@zeelool/icon';

// 类型定义
interface PgSwiperData {
  title: string;
  word_color: string;
}

interface ProductInfo {
  id: number;
  shop_sku: string;
  business_sku: string;
  sku: string;
  short_name: string;
  name: string;
  image: string;
  price_info: any[];
  price_info_v1?: any[];
  price_info_v2?: any[];
  price_info_v3?: any[];
  discount_percent_v1?: number;
  discount_percent_v2?: number;
  discount_percent_v3?: number;
  status: number; // 2 = no restock
  in_stock: string; // '2' = sold out
  stock: number;
  is_wish: number; // 1 = wished, 2 = not wished
  wish_num: number;
}

interface PgSwiperProps {
  pgSwiperData: PgSwiperData;
  productList: ProductInfo[][];
  onProductClick?: (product: ProductInfo, index: number) => void;
  onProductExp?: (product: ProductInfo, index: number) => void;
  appCallbackFnName?: string;
}

interface ProductCardContentProps {
  itemGroup: ProductInfo[];
  groupIndex: number;
  currentProductIndex: Record<number, number>;
  hoverProductIndex: Record<number, number>;
  isWap: boolean;
  currency: any;
  onProductClick?: (product: ProductInfo, index: number) => void;
  onProductExp?: (product: ProductInfo, index: number) => void;
  switchProduct: (groupIndex: number, productIndex: number) => void;
  previewProduct: (groupIndex: number, productIndex: number) => void;
  clearPreview: (groupIndex: number) => void;
  toDetail: (item: ProductInfo) => void;
  getProductPriceInfo: (item: ProductInfo) => any;
  getProductImg: (item: ProductInfo) => string;
  handleWishChange: (item: ProductInfo) => void;
  adaptProductForWish: (product: ProductInfo) => any;
}

// 独立的商品卡片内容组件
const ProductCardContent: React.FC<ProductCardContentProps> = ({
  itemGroup,
  groupIndex,
  currentProductIndex,
  hoverProductIndex,
  isWap,
  currency,
  onProductClick,
  onProductExp,
  switchProduct,
  clearPreview,
  toDetail,
  getProductPriceInfo,
  getProductImg,
  handleWishChange,
  adaptProductForWish,
}) => {
  // 获取当前显示的商品索引（优先使用预览索引）
  const getCurrentProductIndex = (gIndex: number): number => {
    if (!isWap && hoverProductIndex[gIndex] !== undefined) {
      return hoverProductIndex[gIndex];
    }
    return currentProductIndex[gIndex] || 0;
  };

  // 获取当前显示的商品（用于大图显示，支持预览）
  const getCurrentDisplayProduct = (items: ProductInfo[], gIndex: number): ProductInfo => {
    if (!items || !items.length) {
      const emptyProduct: ProductInfo = {
        id: 0,
        shop_sku: '',
        business_sku: '',
        sku: '',
        short_name: '',
        name: '',
        image: '',
        price_info: [],
        status: 0,
        in_stock: '',
        stock: 0,
        is_wish: 2,
        wish_num: 0,
      };
      return emptyProduct;
    }
    const currentIndex = getCurrentProductIndex(gIndex);
    return items[currentIndex] || items[0];
  };

  // 获取当前选中的商品（用于商品信息显示，不受预览影响）
  const getCurrentSelectedProduct = (items: ProductInfo[], gIndex: number): ProductInfo => {
    if (!items || !items.length) {
      const emptyProduct: ProductInfo = {
        id: 0,
        shop_sku: '',
        business_sku: '',
        sku: '',
        short_name: '',
        name: '',
        image: '',
        price_info: [],
        status: 0,
        in_stock: '',
        stock: 0,
        is_wish: 2,
        wish_num: 0,
      };
      return emptyProduct;
    }
    const selectedIndex = currentProductIndex[gIndex] || 0;
    return items[selectedIndex] || items[0];
  };

  const currentSelectedProduct = getCurrentSelectedProduct(itemGroup, groupIndex);
  const currentDisplayProduct = getCurrentDisplayProduct(itemGroup, groupIndex);

  const priceData = getProductPriceInfo(currentSelectedProduct);
  const productImg = getProductImg(currentDisplayProduct);

  // 曝光检测
  const [cardRef, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  const exposedProducts = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (inView && currentSelectedProduct.id) {
      const expKey = `${currentSelectedProduct.id}_${groupIndex}`;
      if (!exposedProducts.current.has(expKey)) {
        exposedProducts.current.add(expKey);
        onProductExp?.(currentSelectedProduct, groupIndex);
      }
    }
  }, [inView, currentSelectedProduct.id, groupIndex, onProductExp]);

  return (
    <a
      ref={cardRef}
      href={`/goods-detail/${currentSelectedProduct.shop_sku}`}
      aria-label={currentSelectedProduct.short_name}
      className="block"
      onClick={(e) => {
        e.preventDefault();
        toDetail(currentSelectedProduct);
        onProductClick?.(currentSelectedProduct, groupIndex);
      }}
    >
      <div
        className="img-wrap relative flex items-center justify-center bg-[#eeeeee] overflow-hidden rounded-[10px]"
        style={{
          height: isWap ? '51.467vw' : '26.041vw',
        }}
      >
        <Image
          className="product-img block w-[80%] mix-blend-multiply mt-[-5px] sm:m-auto md:m-auto lg:m-auto xl:m-auto 2xl:m-auto"
          src={productImg}
          alt={currentDisplayProduct.name}
          width={300}
          height={300}
        />

        {/* SKU 缩略图列表 */}
        {itemGroup && itemGroup.length > 1 && (
          <div
            className="sku-thumbnails w-full absolute left-1/2 transform -translate-x-1/2 flex justify-center bg-[#eeeeee]"
            style={{
              bottom: isWap ? '0px' : '0.3125vw',
              gap: isWap ? '16px' : '1.25vw',
              zIndex: 26,
              paddingBottom: isWap ? '3px' : '0',
            }}
            onMouseLeave={() => clearPreview(groupIndex)}
          >
            {itemGroup.map((skuItem, skuIndex) => (
              <div
                key={skuIndex}
                className="sku-thumb h-auto flex items-center justify-center relative cursor-pointer"
                style={{
                  width: isWap ? '40px' : '3.75vw',
                  paddingBottom: isWap ? '3px' : '0.729vw',
                  paddingTop: isWap ? '10px' : '0px',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  switchProduct(groupIndex, skuIndex);
                  clearPreview(groupIndex);
                }}
                onMouseEnter={() => switchProduct(groupIndex, skuIndex)}
                // onMouseLeave={() => switchProduct(groupIndex, skuIndex)} // 鼠标移出切换商品
              >
                <Image
                  src={getProductImg(skuItem)}
                  alt={`${skuItem.short_name} glasses`}
                  width={isWap ? 40 : 72}
                  height={isWap ? 40 : 72}
                  className="block w-full h-auto mix-blend-multiply"
                />
                {/* Active 状态下划线 - 基于真正选中的索引，不受预览影响 */}
                {(currentProductIndex[groupIndex] || 0) === skuIndex && (
                  <div
                    className="absolute bottom-0 left-1/2 transform -translate-x-1/2 bg-[#282828]"
                    style={{
                      width: isWap ? '16px' : '2.083vw',
                      height: isWap ? '1px' : '2px',
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {/* 收藏按钮 */}
        <div
          className="heartbox h-[24px] absolute flex items-center justify-center"
          style={{
            width: isWap ? '24px' : '24px',
            top: isWap ? '6px' : '0.989vw',
            right: isWap ? '6px' : '0.989vw',
            zIndex: 26,
            borderRadius: isWap ? 'auto' : '16px',
          }}
        >
          <Wish
            data={adaptProductForWish(currentSelectedProduct)}
            className={`${isWap ? 'text-20' : 'text-24'}`}
            iconClassName={`text-${isWap ? '20' : '24'}`}
            iconSize={isWap ? '20px' : '24px'}
            collectScene="坑位收藏"
            wished={() => handleWishChange(currentSelectedProduct)}
            removed={() => handleWishChange(currentSelectedProduct)}
          />
        </div>

        {/* 售罄遮罩 - 基于当前显示的商品状态（包含预览） */}
        {(currentDisplayProduct.status === 2 || currentDisplayProduct.in_stock === '2') && (
          <div className="absolute left-0 right-0 top-0 bottom-0" style={{ zIndex: 25 }}>
            <SoldOutBlock product={currentDisplayProduct} />
          </div>
        )}
      </div>

      {/* 商品信息 */}
      <div
        className="product-info box-border"
        style={{
          padding: isWap ? '8px 8px 0' : '0.833vw 0.416vw 0',
        }}
      >
        <p
          className="name text-[12px] leading-[14px] text-white md:text-[18px] md:leading-[22px] md:font-bb"
          style={{
            marginBottom: isWap ? '2px' : '4px',
          }}
        >
          {currentSelectedProduct.short_name}
        </p>
        <div className="price">
          <span className="now-price text-[14px] font-bb md:font-br text-white md:text-[12px] xl:text-[14px] 2xl:text-[16px]">
            {formatPriceNumberWithSymbol(priceData.sell_price, currency)}
          </span>
          {priceData.sell_price !== priceData.price && (
            <span className="old-price pl-[8px] text-[12px] text-[#A9A9A9] line-through">
              {formatPriceNumberWithSymbol(priceData.price, currency)}
            </span>
          )}
        </div>
      </div>
    </a>
  );
};

const PgSwiper: React.FC<PgSwiperProps> = ({
  pgSwiperData,
  productList,
  onProductClick,
  onProductExp,
}) => {
  const { deviceType } = useContext(ResponsiveContext);
  // const { currency, userInfo } = useStore();
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const { navigate } = useAppNavigation();
  // 记录每个slide中当前显示的商品索引
  const [currentProductIndex, setCurrentProductIndex] = useState<Record<number, number>>({});
  // 记录每个slide中鼠标悬停预览的商品索引（仅PC端生效）
  const [hoverProductIndex, setHoverProductIndex] = useState<Record<number, number>>({});
  // 轮播相关状态
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const swiperRef = useRef<any>(null);

  // 判断是否为移动端
  const isWap = deviceType !== EPlatform.WEB;

  // 切换显示的商品 - 保留useCallback，因为传递给子组件
  const switchProduct = useCallback((groupIndex: number, productIndex: number) => {
    setCurrentProductIndex((prev) => ({
      ...prev,
      [groupIndex]: productIndex,
    }));
  }, []);

  // 预览商品（鼠标悬停时） - 对应Vue版本的previewProduct
  const previewProduct = useCallback(
    (groupIndex: number, productIndex: number) => {
      if (isWap) return; // 移动端不启用hover功能

      setHoverProductIndex((prev) => ({
        ...prev,
        [groupIndex]: productIndex,
      }));
    },
    [isWap],
  );

  // 清除预览状态（鼠标移出时） - 对应Vue版本的clearPreview
  const clearPreview = useCallback(
    (groupIndex: number) => {
      if (isWap) return; // 移动端不启用hover功能

      setHoverProductIndex((prev) => {
        const newState = { ...prev };
        delete newState[groupIndex];
        return newState;
      });
    },
    [isWap],
  );

  // 商品跳转处理
  const toDetail = (item: ProductInfo) => {
    navigate(`/goods-detail/${item.shop_sku}`);
  };

  // 获取商品价格信息 - 依赖userInfo和currency，保留useCallback
  const getProductPriceInfo = useCallback(
    (item: ProductInfo) => {
      try {
        return getPriceInfo(userInfo, item, currency);
      } catch {
        return { price: '', sell_price: '' };
      }
    },
    [userInfo, currency],
  );

  // 获取商品图片 - 不需要useCallback，简单的取值函数
  const getProductImg = (item: ProductInfo): string => {
    return item?.image || '';
  };

  // 处理收藏状态变化 - 不需要useCallback，简单的状态修改
  const handleWishChange = (item: ProductInfo) => {
    // 直接修改原始商品数据的收藏状态
    item.is_wish = item.is_wish === 1 ? 2 : 1;
  };

  // 将ProductInfo转换为Wish组件期望的类型 - 不需要useCallback，简单的转换函数
  const adaptProductForWish = (product: ProductInfo) => {
    return {
      ...product,
      image: {
        product: [product.image || ''],
        material: [],
        celebrity: [],
        video: [],
      },
    } as any; // 使用any暂时解决类型问题
  };

  // 确保Swiper正确的水平布局
  useEffect(() => {
    if (typeof document !== 'undefined') {
      const existingStyle = document.getElementById('pg-swiper-styles');
      if (!existingStyle) {
        const style = document.createElement('style');
        style.id = 'pg-swiper-styles';
        style.textContent = `
          /* Swiper 水平布局 */
          .pg-swiper-container .swiper-wrapper {
            display: flex;
          }
          .pg-swiper-container .swiper-slide {
            flex-shrink: 0;
          }
        `;
        document.head.appendChild(style);
      }
    }
  }, []);

  // 检查是否显示导航按钮
  const showNavigation = useMemo(() => {
    return !isWap && productList.length > 4;
  }, [isWap, productList.length]);

  // Swiper配置 - 基于HotProductSwiper的成功配置
  const swiperOptions = useMemo(() => {
    if (isWap) {
      // 移动端配置：2.5个商品可见
      return {
        modules: [Navigation],
        observer: true,
        observeParents: true,
        observeSlideChildren: true,
        slidesPerView: 2.1,
        spaceBetween: 8,
        slidesPerGroup: 1,
      };
    } else {
      // PC端配置：基于1920px设计稿，使用固定宽度而非slidesPerView
      return {
        modules: [Navigation],
        observer: true,
        observeParents: true,
        observeSlideChildren: true,
        slidesPerView: 4.2,
        spaceBetween: 16, // 对应Vue版本的px2vw(16px)，Swiper配置中保持数字
        slidesPerGroup: 1,
        speed: 300,
        navigation: showNavigation
          ? {
              nextEl: '.pg-swiper-next',
              prevEl: '.pg-swiper-prev',
            }
          : false,
        onSlideChange: (swiper: any) => {
          setIsBeginning(swiper.isBeginning);
          setIsEnd(swiper.isEnd);
        },
        onInit: (swiper: any) => {
          setIsBeginning(swiper.isBeginning);
          setIsEnd(swiper.isEnd);
        },
      };
    }
  }, [showNavigation, isWap]);

  if (!productList.length) {
    return null;
  }

  return (
    <div className="pg-swiper-list relative">
      {/* 标题区域 */}
      <div
        className="word-box px-[14px] pb-[16px] md:px-[48px] md:pb-[32px] flex justify-between items-center"
        style={{
          padding: isWap ? '0 14px 16px' : '0 2.5vw 1.666vw',
        }}
      >
        <h2
          className="title text-[24px] leading-[29px] font-bb text-white text-left md:text-[36px] md:leading-[44px] lg:text-[42px] lg:leading-[50px] xl:text-[48px] xl:leading-[58px]"
          style={{
            color: pgSwiperData.word_color,
          }}
          dangerouslySetInnerHTML={{ __html: pgSwiperData.title }}
        />
        {/* 导航按钮 */}
        {showNavigation && (
          <>
            <div className="flex items-center gap-[16px]">
              {/* Prev Button */}
              <div
                className={`
                pg-swiper-prev  z-20 flex items-center justify-center
                w-[40px] h-[40px] rounded-full border bg-transparent transition-transform duration-200
                ${isBeginning ? 'cursor-not-allowed border-[#8D8D92]' : 'cursor-pointer border-white hover:scale-110'}
              `}
                onClick={() => !isBeginning && swiperRef.current?.swiper?.slidePrev()}
              >
                <LeftArrowOutlined
                  className={`text-[20px] relative right-[1px] ${isBeginning ? 'text-[#8D8D92]' : 'text-white'}`}
                />
              </div>
              {/* Next Button */}
              <div
                className={`
                pg-swiper-next z-20 flex items-center justify-center
                w-[40px] h-[40px] rounded-full border bg-transparent transition-transform duration-200
                ${isEnd ? 'cursor-not-allowed border-[#8D8D92]' : 'cursor-pointer border-white hover:scale-110'}
              `}
                onClick={() => !isEnd && swiperRef.current?.swiper?.slideNext()}
              >
                <RightArrowOutlined
                  className={`text-[20px] relative left-[2px] ${isEnd ? 'text-[#8D8D92]' : 'text-white'}`}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {/* 轮播容器 */}
      <div className="swiper-nav-container relative w-full">
        <Swiper
          ref={swiperRef}
          {...swiperOptions}
          className="pg-swiper-container"
          style={{
            padding: isWap ? '0 14px 50px' : '0 2.5vw 6.145vw',
          }}
        >
          {productList.map((itemGroup, groupIndex) => (
            <SwiperSlide key={groupIndex} className="product-item cursor-pointer">
              <ProductCardContent
                itemGroup={itemGroup}
                groupIndex={groupIndex}
                currentProductIndex={currentProductIndex}
                hoverProductIndex={hoverProductIndex}
                isWap={isWap}
                currency={currency}
                onProductClick={onProductClick}
                onProductExp={onProductExp}
                switchProduct={switchProduct}
                previewProduct={previewProduct}
                clearPreview={clearPreview}
                toDetail={toDetail}
                getProductPriceInfo={getProductPriceInfo}
                getProductImg={getProductImg}
                handleWishChange={handleWishChange}
                adaptProductForWish={adaptProductForWish}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
};

export default PgSwiper;
